import { useCreate, useTranslate, useUpdate } from "@refinedev/core";
import { MediaTemplate, MediaTemplateResponse } from "types/";
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTemplateEditorStoreApi } from "../store/useTemplateEditorStore";

export const useSaveTemplate = (organizationId?: string) => {
  const { mutateAsync: createTemplateMutation } =
    useCreate<MediaTemplateResponse>();
  const { mutateAsync: updateTemplateMutation, isPending: isUpdatingTemplate } =
    useUpdate<MediaTemplateResponse>();
  const queryClient = useQueryClient();
  const storeApi = useTemplateEditorStoreApi();

  const t = useTranslate();

  const updateTemplate = useCallback(
    async (templateId: string, template: MediaTemplate) => {
      if (!organizationId) return;
      await updateTemplateMutation({
        resource: `media/${organizationId}/templates`,
        id: templateId,
        values: { template },
        successNotification: false,
        errorNotification: () => {
          return {
            message: t("components.templates.templatePreview.updateError"),
            type: "error",
          };
        },
      });

      const resource = `media/${organizationId}/templates`;
      queryClient.setQueriesData<MediaTemplateResponse[]>(
        { queryKey: ["templates", resource] },
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((item) =>
            item.id === templateId ? { ...item, template } : item
          );
        }
      );

      // Keep the editor store's serverTemplates snapshot in sync with what we
      // just persisted. It's the read-modify-write base for subsequent template
      // saves, so leaving it stale would drop/resurrect slides changed this
      // session (added defs lost, deleted defs revived).
      storeApi.getState().setServerTemplate(templateId, template);
    },
    [updateTemplateMutation, organizationId, t, queryClient, storeApi]
  );

  const createTemplate = useCallback(
    async (
      template: MediaTemplate,
      successNotification = true,
      errorNotification = true
    ) => {
      if (!organizationId) return;
      try {
        const response = await createTemplateMutation({
          resource: `media/${organizationId}/templates`,
          values: { template },
          successNotification: successNotification
            ? () => {
                return {
                  message: t(
                    "components.templates.templatePreview.saveSuccess"
                  ),
                  type: "success",
                };
              }
            : false,
          errorNotification: errorNotification
            ? () => {
                return {
                  message: t("components.templates.templatePreview.saveError"),
                  type: "error",
                };
              }
            : false,
        });
        return response.data;
      } catch (error) {
        console.error("Error creating template", error);
      }
    },
    [createTemplateMutation, organizationId, t]
  );

  const updateTemplatesSilently = useCallback(
    (templates: { id: string; template: MediaTemplate }[]) => {
      void Promise.all(
        templates.map(({ id, template }) => updateTemplate(id, template))
      ).catch((error) => {
        console.error("Some template updates failed:", error);
      });
    },
    [updateTemplate]
  );

  return {
    updateTemplate,
    isUpdatingTemplate,
    createTemplate,
    updateTemplatesSilently,
  };
};
